interface MainNavProps {
    el: HTMLDivElement;
}

const MAIN_NAV_ITEM_CLASS = 'main-nav-item';
const MAIN_NAV_ITEM_LINK_CLASS = 'main-nav-item-link';
const SUB_NAV_ITEM_CLASS = 'sub-nav-item';
const SUB_NAV_ITEM_LINK_CLASS = 'sub-nav-item-link';
const MAIN_NAV_ACTIVE_CLASS = 'is-active';
const MEGA_MENU_ITEM_CLASS = 'mega-menu-item';
const MEGA_MENU_ITEM_LINK_CLASS = 'mega-menu-item-container';

// For accesibility keyboard main nav
const MainNav = ({ el }: MainNavProps): void => {
    const mainNavLinkEls = el.querySelectorAll(`.${MAIN_NAV_ITEM_LINK_CLASS}`);
    const mainNavEls = el.querySelectorAll(`.${MAIN_NAV_ITEM_CLASS}`);
    const subNavLinkEls = el.querySelectorAll(`.${SUB_NAV_ITEM_LINK_CLASS}`);
    const megaMenuLinkEls = el.querySelectorAll(`.${MEGA_MENU_ITEM_LINK_CLASS}`);

    const closeAllMainNav = (): void => {
        mainNavEls.forEach(mainNavEl => {
            mainNavEl.classList.remove(MAIN_NAV_ACTIVE_CLASS);
            // mainNavEl.setAttribute('aria-expanded', 'false');
        });
    };

    const openMainNav = (linkEl: HTMLElement): void => {
        const parentEl = linkEl.closest(`.${MAIN_NAV_ITEM_CLASS}`);
        if (!parentEl) {
            return;
        }

        const currentSubNavLinkEl = parentEl.querySelector(`.${SUB_NAV_ITEM_LINK_CLASS}`);
        const currentMegaMenuLinkEl = parentEl.querySelector(`.${MEGA_MENU_ITEM_LINK_CLASS}`);
        if (!currentSubNavLinkEl && !currentMegaMenuLinkEl) {
            return;
        }

        // linkEl.setAttribute('aria-expanded', 'true');
        parentEl.classList.add(MAIN_NAV_ACTIVE_CLASS);
        (currentSubNavLinkEl as HTMLAnchorElement)?.focus();
        (currentMegaMenuLinkEl as HTMLAnchorElement)?.focus();
    };

    const closeMainNav = (linkEl: HTMLElement): void => {
        const parentEl = linkEl.closest(`.${MAIN_NAV_ITEM_CLASS}`);
        if (!parentEl) {
            return;
        }

        const currentSubNavLinkEl = parentEl.querySelector(`.${SUB_NAV_ITEM_LINK_CLASS}`);
        const currentMegaMenuLinkEl = parentEl.querySelector(`.${MEGA_MENU_ITEM_LINK_CLASS}`);
        if (!currentSubNavLinkEl && !currentMegaMenuLinkEl) {
            return;
        }

        // linkEl.setAttribute('aria-expanded', 'false');
        parentEl.classList.remove(MAIN_NAV_ACTIVE_CLASS);
    };

    const focusNextElement = (parentEl: HTMLElement, focusElClass: string): void => {
        if (!parentEl.nextElementSibling) {
            return;
        }

        const nextLinkEl = parentEl.nextElementSibling.querySelector(`.${focusElClass}`);
        if (!nextLinkEl) {
            return;
        }

        (nextLinkEl as HTMLAnchorElement).focus();
    };

    const focusPrevElement = (parentEl: HTMLElement, focusElClass: string): void => {
        if (!parentEl.previousElementSibling) {
            return;
        }

        const prevLinkEl = parentEl.previousElementSibling.querySelector(`.${focusElClass}`);
        if (!prevLinkEl) {
            return;
        }

        (prevLinkEl as HTMLAnchorElement).focus();
    };

    const onMainLinkKeyDown = (e: KeyboardEvent): void => {
        const linkEl = e.target as HTMLElement;
        const { key } = e as KeyboardEvent;
        const parentEl: HTMLElement | null = linkEl.closest(`.${MAIN_NAV_ITEM_CLASS}`);
        if (!parentEl) {
            return;
        }

        switch (key) {
        case 'ArrowDown': {
            e.preventDefault();
            closeAllMainNav();
            openMainNav(linkEl);
            break;
        }

        case 'ArrowUp': {
            e.preventDefault();
            closeMainNav(linkEl);
            break;
        }

        case 'ArrowRight': {
            e.preventDefault();
            focusNextElement(parentEl, MAIN_NAV_ITEM_LINK_CLASS);
            break;
        }

        case 'ArrowLeft': {
            e.preventDefault();
            focusPrevElement(parentEl, MAIN_NAV_ITEM_LINK_CLASS);
            break;
        }

        case 'Escape': {
            closeMainNav(linkEl);
            break;
        }

        default:
        }
    };

    const onSubNavLinkKeyDown = (e: KeyboardEvent, subNavItemClass: string, subNavItemLinkClass: string): void => {
        const linkEl = e.target as HTMLElement;
        const { key } = e;
        const parentEl: HTMLElement | null = linkEl.closest(`.${subNavItemClass}`);
        if (!parentEl) {
            return;
        }

        switch (key) {
        case 'ArrowDown': {
            e.preventDefault();
            focusNextElement(parentEl, subNavItemLinkClass);
            break;
        }

        case 'ArrowUp': {
            e.preventDefault();
            if (parentEl.previousElementSibling) {
                focusPrevElement(parentEl, subNavItemLinkClass);
            } else {
                // If goes from subnav to parent nav then set focus to parent
                const mainNavEl = parentEl.closest(`.${MAIN_NAV_ITEM_CLASS}`);
                if (!mainNavEl) {
                    return;
                }

                const mainNavLinkEl = mainNavEl.querySelector(`.${MAIN_NAV_ITEM_LINK_CLASS}`);
                if (!mainNavLinkEl) {
                    return;
                }

                (mainNavLinkEl as HTMLAnchorElement).focus();
            }
            break;
        }

        case 'Escape': {
            closeMainNav(linkEl);
            break;
        }

        default:
        }
    };

    // if the user touches another part of the site close the util
    window.addEventListener('mouseup', e => {
        if (e.target && el !== e.target && !el.contains(e.target as HTMLElement) && !(e.target as HTMLElement).closest('[data-main-nav]')) {
            closeAllMainNav();
        }
    });

    mainNavLinkEls.forEach(mainNavLinkEl => {
        // mainNavLinkEl.setAttribute('aria-expanded', 'false');
        mainNavLinkEl.addEventListener('keydown', (e => {
            onMainLinkKeyDown(e as KeyboardEvent);
        }));
    });

    subNavLinkEls.forEach(subNavLinkEl => {
        subNavLinkEl.addEventListener('keydown', (e => {
            onSubNavLinkKeyDown(e as KeyboardEvent, SUB_NAV_ITEM_CLASS, SUB_NAV_ITEM_LINK_CLASS);
        }));
    });

    megaMenuLinkEls.forEach(megaMenuLinkEl => {
        megaMenuLinkEl.addEventListener('keydown', (e => {
            onSubNavLinkKeyDown(e as KeyboardEvent, MEGA_MENU_ITEM_CLASS, MEGA_MENU_ITEM_LINK_CLASS);
        }));
    });
};

export default MainNav;
